import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

import contactImage from '../../assets/images/contact.jpg';
import './Contact.css';

const HeroSection = styled.section`
  position: relative;
  height: 60vh;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url(${contactImage}) center center/cover no-repeat;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(37, 37, 37, 0.7);
    z-index: 1;
  }

  @media (max-width: 768px) {
    height: 30vh;
    min-height: 250px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const ContactSection = styled.section`
  padding: 6rem 0;
  background: #F6F7F8;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContactInfoSection = styled.div`
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ContactFormSection = styled.div`
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 3px;
    background: #C31321;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
`;

const InfoItem = styled.div`
  margin-bottom: 2rem;
`;

const InfoLabel = styled.div`
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const InfoValue = styled.div`
  color: #64748b;
  line-height: 1.6;
  font-size: 0.9rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border: 1px solid #C31321;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 250px;
  resize: vertical;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border: 1px solid #C31321;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, #C31321 0%, #a50f1a 100%);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: linear-gradient(135deg, #a50f1a 0%, #8a0d15 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(195, 19, 33, 0.3);
  }
`;

const ParkingLocationsSection = styled.section`
  padding: 6rem 0;
  background: white;
`;

const LocationsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }
`;

const LocationsTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: #C31321;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
`;

const MapContainer = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const MapPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 1.1rem;
  text-align: center;
  padding: 2rem;
`;



const Contact = () => {
  return (
    <>
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Contact Us
          </HeroTitle>
                </HeroContent>
      </HeroSection>

      <ContactSection>
        <Container>
          <ContactGrid>
            <ContactInfoSection>
              <SectionTitle
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                Contact Info
              </SectionTitle>
              
              <InfoItem>
                <InfoLabel>Corporate Office Address</InfoLabel>
                <InfoValue>
                  No. 6 Gutenberg Platz<br />
                  51-55 Werner List Street<br />
                  Windhoek, Namibia
                </InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>Customer Service</InfoLabel>
                <InfoValue>
                  +264 85 783 8206<br />
                  +264 61 277 824
                </InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>E-mail</InfoLabel>
                <InfoValue>info@keyplot.com.na</InfoValue>
              </InfoItem>
              
              <InfoItem>
                <InfoLabel>Corporate Office Hours</InfoLabel>
                <InfoValue>Mon - Fri: 8:00am - 5:00pm</InfoValue>
              </InfoItem>
            </ContactInfoSection>
            
            <ContactFormSection>
              <SectionTitle
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                Got a Question?
              </SectionTitle>
              
              <form onSubmit={(e) => e.preventDefault()}>
                <FormGroup>
                  <Input type="text" placeholder="Name" required />
                </FormGroup>
                
                <FormGroup>
                  <Input type="email" placeholder="Email" required />
                </FormGroup>
                
                <FormGroup>
                  <TextArea placeholder="Message" required />
                </FormGroup>
                
                <SubmitButton
                  type="submit"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  Submit
                </SubmitButton>
              </form>
            </ContactFormSection>
                      </ContactGrid>
          </Container>
        </ContactSection>

        <ParkingLocationsSection>
          <LocationsContainer>
            <LocationsTitle
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Parking Locations
            </LocationsTitle>
            
            <MapContainer>
              <MapFrame
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11947.123456789!2d17.0833!3d-22.5609!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1c0d1b3b3b3b3b3%3A0x1c0d1b3b3b3b3b3!2sWindhoek%2C+Namibia!5e0!3m2!1sen!2sna!4v1234567890"
                title="Parking Locations Map"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </MapContainer>
          </LocationsContainer>
        </ParkingLocationsSection>
      </>
    );
  };
  
  export default Contact; 